/* Grubwick admin — UI-facing restaurant + menu types, address helper, and the
 * reference lists (venues, cities, categories) the editor forms select from.
 *
 * The restaurant list itself comes from the backend (src/api/restaurants.ts);
 * this module only owns the shapes those API rows are mapped onto. */
export type VenueType = 'Restaurant' | 'Farmers Market' | 'Micro Kitchen';

export interface MenuItemRecord {
  id: string;
  name: string;
  price: string;
  category: string;
  desc: string;
  /* Backend id of the section this item belongs to, or null when it has no
     section. Read-only in the UI — set by the API mapper and used to reorder
     sections; new/imported items have none. */
  sectionId: string | null;
}

/* A brand location of a restaurant — menus are keyed per location. */
export interface LocationRef {
  id: string;
  name: string;
  address: string;
  city: string;
  itemCount: number;
}

export interface Restaurant {
  id: string;
  name: string;
  venue: VenueType;
  hood: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  style: string;
  dish: string;
  url: string;
  desc: string;
  locations: LocationRef[];
  menu: MenuItemRecord[];
}

/* Join the structured address parts into one "Street, City, State ZIP" line for
   display and search. Tolerates missing parts so partial records still read. */
export function fullAddress(
  r: Pick<Restaurant, 'street' | 'city' | 'state' | 'zip'>,
): string {
  const cityState = [r.city, r.state].filter(Boolean).join(', ');
  const line2 = [cityState, r.zip].filter(Boolean).join(' ');
  return [r.street, line2].filter(Boolean).join(', ');
}

export const VENUES: VenueType[] = ['Restaurant', 'Farmers Market', 'Micro Kitchen'];
export const CITIES: string[] = ['San Diego', 'Los Angeles', 'San Francisco', 'Portland', 'Austin', 'New York'];
export const CATEGORIES: string[] = ['Fried Chicken', 'Sandwich', 'Wings', 'Plates', 'Boxes', 'Sides', 'Drinks', 'Sweets'];

export function newId(prefix = 'id'): string {
  return prefix + '_' + Date.now().toString(36) + Math.floor(Math.random() * 1e4).toString(36);
}

export function blankRestaurant(): Restaurant {
  return {
    id: newId('r'), name: '', venue: 'Restaurant', hood: '',
    street: '', city: 'San Diego', state: '', zip: '',
    style: '', dish: '', url: '', desc: '', locations: [], menu: [],
  };
}
